import { useState } from "react";
import ProgressBar from "../ProgressBar";
import CopyIcon from "../../../src/assests/CopyIcon.svg";
import { useChaseMenu } from "../../store/use-chase-menu";
import { toast } from "react-toastify";
import moment from "moment";
import CustomButton from "../CustomButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faPhone,
  faPlusCircle,
  faRotateRight,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import AddNewLog from "./add-new-log";
import ChaseLogTitle from "./ChaseLogTitle";
import { ParseErrors } from "../../utils/ParseErrors";
import axiosInstance from "../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import downloadFile from "../../utils/downloadFile";
import GetUser from "../../utils/getUser";
import {
  debtSyncStepLabels,
  retryableDebtSyncSteps,
} from "../../utils/data/chase-logs-data";
import ConfirmDialog from "../confirm-dialog";
import { IloanLogs } from "../../types/loan-logs-type";

interface IChaseDropDown {
  UpdateItemData: () => void;
}

type INewLogsTypes = "none" | "create";

const ChaseDropMenu = ({ UpdateItemData }: IChaseDropDown) => {
  const navigate = useNavigate();
  const { initialValues } = useChaseMenu();
  const { data } = initialValues;
  // Both arrive with the chase detail — defaulted so a half-loaded menu renders empty
  // rather than throwing.
  const timeline = initialValues?.timeline ?? [];
  const logsData = initialValues?.logs ?? [];
  const [logs, setLogs] = useState({
    data: logsData,
    totalCount: logsData?.length,
  });
  const [rowtype, setRowType] = useState<INewLogsTypes>("none");
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const isSameCustomerChase = data?.isSameCustomerChase;
  const user = GetUser();
  // retry is for admins and super admins only, the server checks this too
  const canFixSync = ["super-admin", "admin"].includes(user?.type);
  const [syncLoadingId, setSyncLoadingId] = useState<number | null>(null);
  const [retryLog, setRetryLog] = useState<IloanLogs | null>(null);

  // ---------------------------------------------- [ COPY TO CLIPBOARD ] --------------------------------------------------------------
  const CopytoClipboard = () => {
    navigator.clipboard.writeText(
      `myportal.co.uk/${initialValues.data.policyReferenceNumber}`,
    );
    toast.success("Copied");
  };

  // ---------------------------------------------- [ HANDLE TAKE AGENT PAYMENT ] --------------------------------------------------------------
  const HandleTakePayment = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/loan/create-agent-payment-token/${initialValues.data.policyReferenceNumber}`,
      );

      window.open(`${res.data.link}`);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      toast.error(ParseErrors(error));
    }
  };

  const DownloadChaseDocument = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/loan/download/document/${initialValues.data.policyReferenceNumber}`,
      );
      downloadFile(res.data.url);
      setLoading(false);
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
      setLoading(false);
    }
  };

  const loadMoreLogs = async () => {
    setIsFetching(true);
    try {
      const res = await axiosInstance.get(
        `/loan/find/logs/${initialValues.data.id}?limit=${limit}&offset=${
          limit + page
        }`,
      );
      setPage(page + limit);
      setLogs((prevState) => ({
        data: [...prevState.data, ...res.data.data],
        totalCount: res.data.totalCount,
      }));
      setIsFetching(false);
    } catch (error) {
      console.log(error);
      setIsFetching(false);
    }
  };

  /** Retries a failed CDL sync, then reloads the logs so the new status and log show. */
  const HandleRetrySync = async () => {
    if (!retryLog?.id) return;
    const logId = retryLog.id;

    setSyncLoadingId(logId);
    try {
      const res = await axiosInstance.post(`/loan/debt-plan-sync/retry/${logId}`);
      toast.success(res.data.message);
      UpdateItemData();
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      // a failed retry still bumps the retry count, so reload either way
      try {
        const res = await axiosInstance.get(
          `/loan/find/logs/${initialValues.data.id}?limit=${limit}&offset=0`,
        );
        setLogs({ data: res.data.data, totalCount: res.data.totalCount });
        setPage(0);
      } catch (error) {
        console.log(error);
      }
      setSyncLoadingId(null);
      setRetryLog(null);
    }
  };

  /** The exact values the retry re-sends to CDL, read only, so staff know what they are confirming. */
  const renderRetryDetails = (log: IloanLogs) => {
    let syncData: Record<string, any> = {};
    try {
      syncData = JSON.parse(log.syncData ?? "{}");
    } catch {}

    const policyReference = initialValues.data?.policyReferenceNumber;
    const failedReason = log.reason?.replace(`${log.syncStep}: `, "") || "-";
    const sentValues: [string, string][] =
      log.syncStep === "stripe_debt_plan"
        ? [
            ["Policy reference", policyReference],
            ["Debt", `£${Number(syncData.debt ?? 0).toFixed(2)}`],
            ["Number of instalments", String(syncData.months ?? "-")],
            ["Payment day", String(syncData.paymentDay ?? "-")],
            ["Operator code", syncData.updatedBy ? "Code of the agent who set up the plan" : "None"],
          ]
        : [
            ["Policy reference", policyReference],
            ["Transaction reference", syncData.transactionReference ?? "-"],
            ["Amount", `£${Number(syncData.amount ?? 0).toFixed(2)}`],
          ];

    const renderRow = ([label, value]: [string, string]) => (
      <span key={label} className="flex justify-between gap-4 py-1 border-b border-edge-faint last:border-none">
        <span className="text-light-gray">{label}</span>
        <span className="text-text-primary font-semibold text-end break-all">{value}</span>
      </span>
    );

    return (
      <span className="block text-[14px]">
        <span className="block">
          This sends the {debtSyncStepLabels[log.syncStep ?? ""]?.toLowerCase()} again, once, with the values below.
        </span>
        <span className="block mt-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-light-gray">
          Sync details
        </span>
        <span className="block mt-1">
          {(
            [
              ["CDL call", debtSyncStepLabels[log.syncStep ?? ""] ?? "-"],
              ["Failed on", moment(log.createdAt).format("DD/MM/YYYY HH:mm")],
              ["Why it failed", failedReason],
              ["Retries so far", String(log.attempt ?? 0)],
            ] as [string, string][]
          ).map(renderRow)}
        </span>
        <span className="block mt-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-light-gray">
          Values sent to CDL
        </span>
        <span className="block mt-1">{sentValues.map(renderRow)}</span>
      </span>
    );
  };

  const renderSyncActions = (item: IloanLogs) => {
    if (item.type !== "debt-plan-sync-failed" || !item.syncStatus) return null;

    if (item.syncStatus === "resolved") {
      return (
        <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.08em] text-emerald-600 dark:text-emerald-300 border border-emerald-500/40 rounded-full px-2 py-[3px]">
          Fixed
        </span>
      );
    }

    const isBusy = syncLoadingId === item.id;
    const canRetry =
      item.syncStatus === "failed" &&
      Boolean(item.syncData) &&
      retryableDebtSyncSteps.includes(item.syncStep ?? "");

    return (
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-rose-600 dark:text-rose-300 border border-rose-500/40 rounded-full px-2 py-[3px]">
          {item.syncStatus === "retrying" ? "Retrying" : "Not fixed"}
          {item.attempt ? ` · ${item.attempt} ${item.attempt === 1 ? "retry" : "retries"}` : ""}
        </span>
        {canFixSync && canRetry ? (
          <button
            type="button"
            disabled={isBusy}
            onClick={() => setRetryLog(item)}
            className="flex items-center gap-1.5 text-[12px] font-semibold text-accent hover:opacity-80 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRotateRight} spin={isBusy} />
            Retry
          </button>
        ) : null}
      </div>
    );
  };

  // ---------------------------------------------- [ TOGGLE NEW ROW ] --------------------------------------------------------------
  const onUpdateLogsRowType = (type: INewLogsTypes) => {
    setRowType(type);
  };

  return (
    <td colSpan={10} className="p-0">
      {retryLog ? (
        <ConfirmDialog
          title="Are you sure you want to retry this CDL sync?"
          description={renderRetryDetails(retryLog)}
          confirmLabel="Retry sync"
          tone="danger"
          loading={syncLoadingId === retryLog.id}
          onConfirm={HandleRetrySync}
          onCancel={() => setRetryLog(null)}
        />
      ) : null}
      <div className="bg-overlay border-y border-edge-faint px-5 py-5 max-sm:px-2 max-sm:py-3 animate-fade-in max-lg:sticky max-lg:left-0 max-lg:w-[calc(100vw-98px)] max-sm:w-[calc(100vw-58px)]">
        <div className="w-full bg-surface rounded-2xl border border-edge p-5 max-sm:p-3 sm:p-6">
          <div className="flex flex-col lg:flex-row items-start gap-6">
            <div className="w-full lg:flex-1 min-w-0">
              <div className="w-full flex items-center justify-between gap-4">
                <p className="flex items-center gap-2 text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                  Chase Timeline
                  {data?.QueuedCallId ? (
                    <FontAwesomeIcon
                      icon={faPhone}
                      className="text-[11px] text-text-secondary"
                      title="Queued call"
                    />
                  ) : null}
                </p>

                {initialValues?.data?.status === "Chargebacks" ? (
                  <CustomButton
                    disabled={loading}
                    className="!h-9 !px-4 !text-[13px] bg-gradient-to-r from-emerald-500 to-emerald-600 text-white border border-white/10 shadow-lg shadow-emerald-500/20 hover:shadow-glow-sm hover:-translate-y-0.5 rounded-lg cursor-pointer transition-all duration-300"
                    onClick={DownloadChaseDocument}
                  >
                    Download Document
                  </CustomButton>
                ) : null}
              </div>

              <ProgressBar
                timeline={[...timeline].sort((a, b) => a.day - b.day)}
              />
            </div>

            <div className="w-full lg:w-[260px] shrink-0">
              <div className="w-full h-fit rounded-xl px-4 py-3.5 bg-surface-elevated border border-edge">
                <p className="text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]">
                  Payment Link
                </p>
                <div className="flex items-center justify-between gap-3 mt-2">
                  <h1
                    className="text-[11px] text-blue font-bold cursor-pointer text-nowrap flex-1 overflow-hidden text-ellipsis hover:underline"
                    title={`myportal.co.uk/${initialValues?.data?.policyReferenceNumber}`}
                    onClick={() => {
                      window.open(
                        `https://myportal.co.uk/${initialValues?.data?.policyReferenceNumber}`,
                        "_blank",
                      );
                    }}
                  >
                    myportal.co.uk/{initialValues?.data?.policyReferenceNumber}
                  </h1>
                  <div className="shrink-0" onClick={CopytoClipboard}>
                    <div
                      className="bg-active-background w-8 h-8 rounded-full flex justify-center items-center cursor-pointer border border-edge hover:border-indigo-400/40 transition-colors duration-200"
                      title="Copy payment link"
                    >
                      <img className="w-3 h-4" src={CopyIcon} alt="" />
                    </div>
                  </div>
                </div>
              </div>

              <CustomButton
                className="!w-full !mt-2 !h-[42px]"
                style={{ width: "100%" }}
                variant="primary"
                onClick={HandleTakePayment}
                disabled={loading}
              >
                £ Take Payment
              </CustomButton>
            </div>
          </div>
        </div>

        {/* --------------------------------------------------------- IF SAME CUSTOMER ---------------------------------------------------------------------- */}
        {isSameCustomerChase ? (
          <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 px-4 py-3 mt-4 flex items-center justify-between gap-4 flex-wrap">
            <p className="flex items-center gap-2.5 text-[13px] font-medium text-text-secondary">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="text-amber-500"
              />
              Another chase exists for this customer
            </p>
            <button
              type="button"
              className="shrink-0 flex items-center gap-1.5 text-[13px] font-semibold text-accent hover:underline cursor-pointer"
              onClick={() => {
                navigate(
                  `/chaselist?query=${data?.firstName + " " + data?.surname}`,
                );
              }}
            >
              View details
              <FontAwesomeIcon
                icon={faArrowUpRightFromSquare}
                className="text-[11px]"
              />
            </button>
          </div>
        ) : null}

        {/* ------------------------------------------------------- IF CHASE RESTART ------------------------------------------------------------------ */}
        {initialValues?.data?.restartDate ? (
          <div className="rounded-xl border border-edge bg-surface px-4 py-3 mt-3">
            <p className="flex items-center gap-2.5 text-[13px] font-medium text-text-secondary">
              <FontAwesomeIcon icon={faRotateRight} className="text-accent" />
              Chase restarts {moment(initialValues?.data?.restartDate).fromNow()}{" "}
              on{" "}
              <span className="font-bold text-text-primary">
                {moment(initialValues?.data?.restartDate).format("DD MMM YYYY")}
              </span>
            </p>
          </div>
        ) : null}

        <div className="w-full bg-surface rounded-2xl border border-edge p-5 sm:p-6 mt-4">
          <div className="w-full">
            {/* Column labels sit at the same weight as the card's own eyebrow, so the
                list reads as one block instead of a heading stacked on a heading. */}
            <div className="flex items-center justify-between gap-4">
              <p className="flex items-center gap-2 text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]">
                <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                Debt Timeline
              </p>
              <div className="flex items-center gap-4 shrink-0 text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]">
                <span className="w-[80px] sm:w-[96px] text-end">Amount</span>
                <span className="w-[80px] sm:w-[96px] text-end">Collected</span>
                <span className="w-[80px] sm:w-[96px] text-end">Outstanding</span>
              </div>
            </div>

            <div className="mt-2">
              {logs?.data?.map((item) => {
                return (
                  <div
                    className="flex items-start sm:items-center justify-between gap-4 py-3.5 border-b border-edge-faint last:border-none"
                    key={item?.id}
                  >
                    <h2 className="w-[86px] sm:w-[100px] text-[12px] font-semibold text-light-gray text-start shrink-0 tabular-nums">
                      {moment(item.createdAt).format("DD/MM/YYYY")}
                    </h2>

                    <ChaseLogTitle item={item} />

                    {renderSyncActions(item)}

                    {/* -------------------------------------------------------- [ IF CHASE CREATED OR UPDATED  ] ----------------------------------------------- */}

                    <div className="flex items-start sm:items-center gap-4 shrink-0">
                      <span className="w-[80px] sm:w-[96px] text-[13px] sm:text-[14px] font-semibold text-light-gray text-end shrink-0 tabular-nums">
                        £{item.totalAmount?.toFixed(2) ?? " -"}
                      </span>
                      <span className="w-[80px] sm:w-[96px] text-[13px] sm:text-[14px] font-semibold text-emerald-600 dark:text-emerald-300 text-end shrink-0 tabular-nums">
                        £{item.collectedAmount?.toFixed(2) ?? " -"}
                      </span>
                      <span className="w-[80px] sm:w-[96px] text-[13px] sm:text-[14px] font-semibold text-heading-color text-end shrink-0 tabular-nums">
                        £{item.outstandingBalance?.toFixed(2) ?? " -"}
                      </span>
                    </div>
                  </div>
                );
              })}

              {logsData?.length >= 10 && page <= logs.totalCount ? (
                <div className="flex items-center justify-center pt-3">
                  <button
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold text-text-secondary border border-edge hover:border-indigo-400/40 hover:text-text-primary transition-colors cursor-pointer disabled:opacity-60"
                    onClick={loadMoreLogs}
                    disabled={isFetching}
                  >
                    <FontAwesomeIcon
                      icon={faRotateRight}
                      className="text-[12px]"
                      spin={isFetching}
                    />
                    Load more
                  </button>
                </div>
              ) : null}
            </div>

            {rowtype === "create" ? (
              <AddNewLog
                onUpdateLogsRowType={onUpdateLogsRowType}
                UpdateItemData={UpdateItemData}
              />
            ) : null}

            {/* ---------------------------------------------------- ADD NEW ROW ---------------------------------------------------------------------------------------
                Hidden while the draft form is open — offering "Add row" underneath an
                already-open row invited a second one and read as a second action. */}
            {rowtype !== "create" ? (
              <button
                type="button"
                className="w-full py-3 bg-transparent hover:bg-overlay-hover border border-dashed border-edge hover:border-indigo-400/40 rounded-xl mt-3 cursor-pointer flex items-center justify-center gap-2 text-light-gray hover:text-text-primary transition-all duration-200"
                onClick={() => onUpdateLogsRowType("create")}
              >
                <FontAwesomeIcon icon={faPlusCircle} className="text-[13px]" />
                <span className="text-[13px] font-semibold">Add row</span>
              </button>
            ) : null}
            {/* ---------------------------------------------------- ADD NEW ROW --------------------------------------------------------------------------------------- */}
          </div>
        </div>
      </div>
    </td>
  );
};

export default ChaseDropMenu;
